import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Select, Option } from '@carvana/showroom-forms/Select';
import { useContext, useEffect, useState } from 'react';
import { useSubsequentEffect } from 'packages/react-hooks/src';
import { PrimaryButton } from '~/components/common/Button';
import FormType from '~/constants/sem/FormTypes';
import { FilterInput, MakeFilter } from '~/contracts/common';
import { SemHeroModule, SemId } from '~/contracts/sem';
import generateSearchLinkFilter from '~/utilities/generateSearchFilterLink';

type ModelOptions = {
  __typename?: 'SEMHeroModuleDropdownOption';
  id: string;
  title?: string | null;
};

type OwnProps = Partial<SemHeroModule['variant']> & {
  semModularRequest: SemId;
};

type HandleChangeType = (e: React.ChangeEvent<HTMLInputElement>) => void;

const constructSearchLink = (
  semModularRequest: SemId,
  make = '',
  model = ''
) => {
  const { request } = semModularRequest;
  const zip5 = semModularRequest.request?.zip5 || '';
  const newFilters: FilterInput = { filters: {} };

  if (request) {
    Object.assign(newFilters.filters, { ...request.filters });

    if (make) {
      const makeFilter: MakeFilter = {
        name: make
      };
      if (model) {
        makeFilter.models = [{ name: model }];
      }
      newFilters.filters.makes = [makeFilter];
    }
  }

  return generateSearchLinkFilter(newFilters, zip5);
};

function MakeModelForm({
  buttonText,
  dropdownOptions,
  formType,
  semModularRequest
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const [makeSelection, setMakeSelection] = useState(
    formType === FormType.Models && dropdownOptions?.length
      ? dropdownOptions[0]?.title ?? ''
      : ''
  );
  const [modelSelection, setModelSelection] = useState('');
  const [modelData, setModelData] = useState<Array<ModelOptions | null>>([]);
  const [searchLink, setSearchLink] = useState(
    constructSearchLink(semModularRequest)
  );

  useEffect(() => {
    const selectedMake = dropdownOptions?.find(
      make => make?.title === makeSelection
    );

    setModelSelection('');
    setModelData(selectedMake?.options ?? []);
  }, [dropdownOptions, makeSelection]);

  useSubsequentEffect(() => {
    setSearchLink(
      constructSearchLink(semModularRequest, makeSelection, modelSelection)
    );
  }, [makeSelection, modelSelection, semModularRequest]);

  const handleMakeChange: HandleChangeType = e => {
    setMakeSelection(e.target.value);

    trackEvent('Selected - Hero - Search Form Make', {
      make: e.target.value
    });
  };

  const handleModelChange: HandleChangeType = e => {
    setModelSelection(e.target.value);

    trackEvent('Selected - Hero - Search Form Model', {
      model: e.target.value
    });
  };

  return (
    <>
      <div className="mt-24 flex gap-8">
        {formType === FormType.MakesModels && (
          <Select
            id="with-label"
            value={makeSelection}
            onChange={e => handleMakeChange(e)}
            label="Make"
          >
            {dropdownOptions?.map(make => (
              <Option key={make?.id} value={make?.title}>
                {make?.title}
              </Option>
            ))}
          </Select>
        )}
        <Select
          id="with-label"
          value={modelSelection}
          onChange={e => handleModelChange(e)}
          label="Model"
          disabled={!makeSelection}
        >
          {modelData?.map(model => (
            <Option key={model?.id} value={model?.title}>
              {model?.title}
            </Option>
          ))}
        </Select>
      </div>
      <a
        href={searchLink}
        data-analytics-id="Hero - Search Form CTA"
        data-analytics-attributes={JSON.stringify({
          title: buttonText,
          linkUrl: searchLink
        })}
        className="no-underline"
      >
        <PrimaryButton className="mt-16 w-full m:mt-32">
          {buttonText}
        </PrimaryButton>
      </a>
    </>
  );
}

export default MakeModelForm;
